import { useCallback, useMemo, useState } from 'react';
import { message } from 'antd';
import { p5m3Api, P5M3Candidate } from '../../../services/p5m3Api';

export interface P5M3Row {
  item: P5M3Candidate;
  selected: boolean;
}

const errorMessage = (error: unknown, fallback: string): string => {
  const candidate = error as { response?: { data?: { error?: string } } };
  return candidate.response?.data?.error || fallback;
};

export function useP5M3() {
  const [rows, setRows] = useState<P5M3Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  const query = useCallback(async (start: string, end: string) => {
    setLoading(true);
    try {
      const response = await p5m3Api.query(start.trim(), end.trim());
      setLastApiResponse(response);
      setRows((response.items || []).map((item) => ({ item, selected: false })));
      setQueried(true);
      if (!response.items?.length) message.info('查無可退核的物化性測試報告');
    } catch (error) {
      message.error(errorMessage(error, '查詢失敗'));
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = useCallback((reportNo: string, selected: boolean) => {
    setRows((current) => current.map((row) =>
      row.item.fp230_01 === reportNo ? { ...row, selected } : row
    ));
  }, []);

  const selectAll = useCallback((selected: boolean) => {
    setRows((current) => current.map((row) => ({ ...row, selected })));
  }, []);

  const reverse = useCallback(async (start: string, end: string) => {
    const selectedNos = rows.filter((row) => row.selected).map((row) => row.item.fp230_01);
    if (selectedNos.length === 0) {
      message.warning('請至少勾選一筆報告');
      return false;
    }
    setReverseLoading(true);
    try {
      const response = await p5m3Api.reverse(selectedNos);
      setLastApiResponse(response);
      message.success(response.message || `已完成 ${response.success} 筆退核`);
      await query(start, end);
      return true;
    } catch (error) {
      message.error(errorMessage(error, '退核失敗，資料未變更'));
      return false;
    } finally {
      setReverseLoading(false);
    }
  }, [query, rows]);

  const selectedCount = useMemo(
    () => rows.filter((row) => row.selected).length,
    [rows]
  );

  return {
    rows,
    loading,
    reverseLoading,
    queried,
    selectedCount,
    lastApiResponse,
    query,
    toggle,
    selectAll,
    reverse,
  };
}
